import React, { useCallback, useMemo, useState } from 'react';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { useDashboardTileDetail } from '../useDashboardTileDetail';
import { DashboardDetailDrawer } from '../dashboardDetailDrawer';
import { FulfillDutyAction } from '../fulfillDutyAction';
import { dutyOfRowHref, dutyRowHref } from '../dutyLink';

// rows that are (or belong to) a duty open it on the schedule in a new tab
const ROW_HREF = { schedule: dutyRowHref, pendingTask: dutyOfRowHref, pendingCheckpoint: dutyOfRowHref };
import {
  getActiveUserDetailQuery, getEmployeeDashboardDetailQuery, getPendingCheckpointDetailQuery,
  getPendingTaskDetailQuery, getScheduleDashboardDetailQuery,
} from '@src/util/query/dashboard.query';

// The record lists behind the tiles. Every schedule-based tile (duties, hours, app usage,
// uninvoiced duties) shares one schedule list since they all resolve to a set of duty ids.
export const scheduleColumns = (onFulfilled) => [
  { key: 'site', label: 'Site', width: '1.4fr', render: (row) => row?.siteName || '-' },
  { key: 'date', label: 'Date', width: '1.3fr', render: (row) => row?.firstDate ? dayjs(row.firstDate).format('DD MMM YYYY, HH:mm') : '-' },
  { key: 'hours', label: 'Hours', width: '0.7fr', render: (row) => (typeof row?.totalHours === 'number' ? row.totalHours.toFixed(2) : (row?.totalHours || '-')) },
  { key: 'assignee', label: 'Assigned To', width: '1.2fr', render: (row) => row?.employeeName || row?.agencyName || 'Unassigned' },
  { key: 'status', label: 'Status', width: '1.3fr', render: (row) => <FulfillDutyAction row={row} onFulfilled={onFulfilled} /> },
];

export const employeeColumns = [
  { key: 'name', label: 'Employee', width: '1.6fr', render: (row) => `${row?.firstname || ''} ${row?.lastname || ''}`.trim() || '-' },
  { key: 'email', label: 'Email', width: '1.6fr', render: (row) => row?.email || '-' },
  { key: 'phone', label: 'Phone', width: '1fr', render: (row) => row?.phone || '-' },
];

export const pendingTaskColumns = [
  { key: 'task', label: 'Task', width: '1.4fr', render: (row) => row?.label || '-' },
  { key: 'site', label: 'Site', width: '1.2fr', render: (row) => row?.siteName || '-' },
  { key: 'employee', label: 'Assigned To', width: '1.2fr', render: (row) => row?.employeeName || 'Unassigned' },
  { key: 'due', label: 'Due', width: '1.2fr', render: (row) => row?.requiredAt ? dayjs(row.requiredAt).format('DD MMM YYYY, HH:mm') : '-' },
  { key: 'flag', label: '', width: '0.8fr', render: (row) => row?.expired ? <Tag color="red">Expired</Tag> : (row?.late ? <Tag color="orange">Late</Tag> : null) },
];

// checkpoints are set up per site as a Form Template or Custom Fields (SiteCheckpoint.responseFlow)
export const checkpointColumns = [
  { key: 'checkpoint', label: 'Checkpoint', width: '1.4fr', render: (row) => row?.label || '-' },
  { key: 'site', label: 'Site', width: '1.2fr', render: (row) => row?.siteName || '-' },
  {
    key: 'type', label: 'Type', width: '1.1fr',
    render: (row) => row?.responseFlow === 'form'
      ? <Tag color="blue">Form Template</Tag>
      : row?.responseFlow === 'fields' ? <Tag color="purple">Custom Fields</Tag> : '-',
  },
  { key: 'employee', label: 'Assigned To', width: '1.2fr', render: (row) => row?.employeeName || 'Unassigned' },
  { key: 'due', label: 'Due', width: '1.2fr', render: (row) => row?.requiredAt ? dayjs(row.requiredAt).format('DD MMM YYYY, HH:mm') : '-' },
  { key: 'flag', label: '', width: '0.7fr', render: (row) => row?.expired ? <Tag color="red">Expired</Tag> : (row?.late ? <Tag color="orange">Late</Tag> : null) },
];

// "App Usage": real evidence (a completed Clock In, or a mobile-tagged confirm) - not the duty's
// status, since admins can force status "1" too
export const appUsageColumns = [
  { key: 'site', label: 'Site', width: '1.3fr', render: (row) => row?.siteName || '-' },
  { key: 'employee', label: 'Employee', width: '1.3fr', render: (row) => row?.employeeName || 'Unassigned' },
  { key: 'date', label: 'Date', width: '1.3fr', render: (row) => row?.firstDate ? dayjs(row.firstDate).format('DD MMM YYYY, HH:mm') : '-' },
  { key: 'used', label: 'Used App', width: '1fr', render: (row) => row?.usedApp ? <div style={{ color: 'green' }}>Yes</div> : <div style={{ color: 'orange' }}>No</div> },
];

export const activeUserColumns = [
  { key: 'name', label: 'User', width: '1.6fr', render: (row) => `${row?.firstname || ''} ${row?.lastname || ''}`.trim() || '-' },
  { key: 'email', label: 'Email', width: '1.8fr', render: (row) => row?.email || '-' },
  { key: 'role', label: 'Access', width: '1fr', render: (row) => row?.userAccessType || '-' },
  { key: 'status', label: 'Status', width: '1fr', render: (row) => row?.isActive ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag> },
  { key: 'lastActive', label: 'Last Active', width: '1.2fr', render: (row) => row?.lastActive ? dayjs(row.lastActive).format('DD MMM YYYY, HH:mm') : '-' },
];

/**
 * One drawer for every widget. `openDetail(kind, ...)` pages through a detail query by the ids
 * the tile already has; `openRows(...)` shows rows the widget already loaded.
 */
export const useDetailHost = ({ onDutyChanged }) => {
  const hooks = {
    schedule: useDashboardTileDetail(getScheduleDashboardDetailQuery, 'getScheduleDashboardDetail'),
    employee: useDashboardTileDetail(getEmployeeDashboardDetailQuery, 'getEmployeeDashboardDetail'),
    pendingTask: useDashboardTileDetail(getPendingTaskDetailQuery, 'getPendingTaskDetail'),
    pendingCheckpoint: useDashboardTileDetail(getPendingCheckpointDetailQuery, 'getPendingCheckpointDetail'),
    activeUser: useDashboardTileDetail(getActiveUserDetailQuery, 'getActiveUserDetail'),
  };
  const [active, setActive] = useState(null);

  const openDetail = useCallback((kind, { title, columns, emptyText, variables }) => {
    const hook = hooks[kind];
    if (!hook) return;
    setActive({ kind, title, columns, emptyText });
    hook.open(variables || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hooks.schedule.open, hooks.employee.open, hooks.pendingTask.open, hooks.pendingCheckpoint.open, hooks.activeUser.open]);

  const openRows = useCallback(({ title, columns, rows, emptyText, rowHref }) => {
    setActive({ kind: 'rows', title, columns, rows, emptyText, rowHref });
  }, []);

  const fulfilled = useCallback((id, patch) => {
    hooks.schedule.patchRow(id, patch);
    onDutyChanged?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hooks.schedule.patchRow, onDutyChanged]);

  const columns = useMemo(() => {
    if (!active) return [];
    return typeof active.columns === 'function' ? active.columns(fulfilled) : active.columns;
  }, [active, fulfilled]);

  const source = active?.kind === 'rows'
    ? { rows: active.rows || [], loading: false, hasMore: false, totalCount: (active.rows || []).length, loadMore: () => {} }
    : active ? hooks[active.kind] : null;

  const drawer = active && source ? (
    <DashboardDetailDrawer
      open
      title={active.title}
      totalCount={source.totalCount}
      onClose={() => setActive(null)}
      columns={columns}
      rows={source.rows}
      loading={source.loading}
      hasMore={source.hasMore}
      onLoadMore={source.loadMore}
      emptyText={active.emptyText}
      rowHref={active.kind === 'rows' ? active.rowHref : ROW_HREF[active.kind]}
    />
  ) : null;

  // rows handed to openRows are a snapshot - let a widget swap in fresh ones while it's open
  const updateRows = useCallback((title, rows) => {
    setActive((current) => (current?.kind === 'rows' && current.title === title ? { ...current, rows } : current));
  }, []);

  return { openDetail, openRows, updateRows, drawer };
};
